import React, { useState, useEffect, useCallback } from 'react';
import { HomePage } from './pages/HomePage';
import { RoomPage } from './pages/RoomPage';
import { AdminPage } from './pages/AdminPage';
import { PseudoSetup } from './pages/PseudoSetup';
import { useTimerStore } from './stores/timerStore';
import { useKonamiCode } from './hooks/useKonamiCode';
import type { User } from './types';

export default function App() {
  const { user, setUser } = useTimerStore();
  const [currentRoomId, setCurrentRoomId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('room');
    }
    return null;
  });

  // Check URL immediately on mount for admin route/param
  const [isAdminOpen, setIsAdminOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return (
        params.get('admin') === 'true' ||
        params.has('admin') ||
        window.location.pathname === '/admin' ||
        window.location.pathname.startsWith('/admin')
      );
    }
    return false;
  });

  // Konami Code trigger (Haut, Haut, Bas, Bas, Gauche, Droite)
  const { progress, pushDirection } = useKonamiCode({
    onSuccess: () => {
      setIsAdminOpen(true);
    },
  });

  // Load user from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('namm-user');
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {
        // Invalid stored user, will show setup
      }
    }
  }, [setUser]);


  // Update URL when room changes
  useEffect(() => {
    if (isAdminOpen) return;
    if (currentRoomId) {
      const url = new URL(window.location.href);
      url.searchParams.set('room', currentRoomId);
      window.history.pushState({}, '', url.toString());
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete('room');
      window.history.pushState({}, '', url.toString());
    }
  }, [currentRoomId, isAdminOpen]);

  const handleCloseAdmin = useCallback(() => {
    setIsAdminOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('admin');
    if (url.pathname === '/admin' || url.pathname.startsWith('/admin')) {
      url.pathname = '/';
    }
    window.history.pushState({}, '', url.toString());
  }, []);

  const handleSetupComplete = (newUser: User) => {
    setUser(newUser);
  };

  const handleJoinRoom = (roomId: string) => {
    setCurrentRoomId(roomId);
  };

  const handleLeaveRoom = () => {
    setCurrentRoomId(null);
  };

  // If Admin view is active
  if (isAdminOpen) {
    return (
      <AdminPage
        onClose={handleCloseAdmin}
        onNavigateToRoom={(roomId) => {
          setCurrentRoomId(roomId);
          handleCloseAdmin();
        }}
      />
    );
  }

  // Step 1: Pseudo setup
  if (!user) {
    return (
      <PseudoSetup
        onComplete={handleSetupComplete}
        onDirectionClick={pushDirection}
        progressLength={progress.length}
      />
    );
  }

  // Step 2: Room selection or room view
  if (currentRoomId) {
    return (
      <RoomPage
        roomId={currentRoomId}
        onLeave={handleLeaveRoom}
        onDirectionClick={pushDirection}
        progressLength={progress.length}
      />
    );
  }

  return (
    <HomePage
      onJoinRoom={handleJoinRoom}
      onDirectionClick={pushDirection}
      progressLength={progress.length}
    />
  );
}
